<script setup lang="ts">
/**
 * FilterSelect — Custom styled select dropdown for QuickFilterPanel.
 *
 * Replaces native <select> to allow full CSS control over the
 * dropdown options panel (native options can't be styled cross-browser).
 */
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

interface Option {
    value: string
    label: string
}

const props = defineProps<{
    modelValue: string
    options: Option[]
    placeholder?: string
    label?: string
}>()

const emit = defineEmits<{
    'update:modelValue': [value: string]
}>()

const isOpen = ref(false)
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const highlightIndex = ref(-1)
const dropdownStyle = ref<Record<string, string>>({})

const selectedLabel = computed(() => {
    if (!props.modelValue) return ''
    const opt = props.options.find(o => o.value === props.modelValue)
    return opt?.label ?? props.modelValue
})

const displayText = computed(() => selectedLabel.value || props.placeholder || 'All')
const hasValue = computed(() => !!props.modelValue)

async function toggle() {
    isOpen.value = !isOpen.value
    if (isOpen.value) {
        highlightIndex.value = props.options.findIndex(o => o.value === props.modelValue)
        await nextTick()
        positionDropdown()
    }
}

function positionDropdown() {
    if (!triggerRef.value) return
    const rect = triggerRef.value.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const menuHeight = Math.min((props.options.length + 1) * 32 + 10, 240)
    const openAbove = spaceBelow < menuHeight && rect.top > menuHeight

    dropdownStyle.value = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 160)}px`,
        zIndex: '9999',
        ...(openAbove
            ? { bottom: `${window.innerHeight - rect.top + 2}px` }
            : { top: `${rect.bottom + 2}px` }),
    }
}

function select(value: string) {
    emit('update:modelValue', value)
    isOpen.value = false
}

function clear() {
    emit('update:modelValue', '')
    isOpen.value = false
}

function onKeydown(e: KeyboardEvent) {
    if (!isOpen.value) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
        }
        return
    }
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        highlightIndex.value = Math.min(highlightIndex.value + 1, props.options.length - 1)
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        highlightIndex.value = Math.max(highlightIndex.value - 1, -1)
    } else if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightIndex.value === -1) {
            clear()
        } else {
            const opt = props.options[highlightIndex.value]
            if (opt) select(opt.value)
        }
    } else if (e.key === 'Escape') {
        isOpen.value = false
    }
}

function onClickOutside(event: MouseEvent) {
    const target = event.target as Node
    if (!triggerRef.value?.contains(target) && !dropdownRef.value?.contains(target)) {
        isOpen.value = false
    }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<template>
    <button
        ref="triggerRef"
        type="button"
        :class="['fs-trigger', { 'fs-trigger--active': hasValue }]"
        @click="toggle"
        @keydown="onKeydown"
    >
        <span :class="['fs-trigger__text', { 'fs-trigger__text--placeholder': !hasValue }]">{{ displayText }}</span>
        <svg class="fs-trigger__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9" />
        </svg>
    </button>

    <Teleport to="body">
        <Transition name="fs-drop">
            <div v-if="isOpen" ref="dropdownRef" class="fs-dropdown" :style="dropdownStyle">
                <!-- Clear / All option -->
                <button
                    :class="['fs-option', { 'fs-option--highlighted': highlightIndex === -1, 'fs-option--selected': !modelValue }]"
                    @click="clear"
                    @mouseenter="highlightIndex = -1"
                >
                    <span class="fs-option__label">{{ placeholder || 'All' }}</span>
                    <svg v-if="!modelValue" class="fs-option__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </button>
                <button
                    v-for="(opt, i) in options"
                    :key="opt.value"
                    :class="['fs-option', {
                        'fs-option--highlighted': i === highlightIndex,
                        'fs-option--selected': opt.value === modelValue,
                    }]"
                    @click="select(opt.value)"
                    @mouseenter="highlightIndex = i"
                >
                    <span class="fs-option__label">{{ opt.label }}</span>
                    <svg v-if="opt.value === modelValue" class="fs-option__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.fs-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    padding-right: 1.5rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    cursor: pointer;
    transition: border-color 0.15s;
    position: relative;
    text-align: left;
}

.fs-trigger:hover {
    border-color: var(--border-color-hover, var(--border-color));
}

.fs-trigger:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.1);
}

.fs-trigger--active {
    border-color: var(--color-primary);
}

.fs-trigger__text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.fs-trigger__text--placeholder {
    color: var(--text-disabled);
}

.fs-trigger__chevron {
    position: absolute;
    right: 0.375rem;
    width: 12px;
    height: 12px;
    color: var(--text-muted);
    flex-shrink: 0;
}

/* Dropdown */
.fs-dropdown {
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    padding: 0.25rem;
    max-height: 240px;
    overflow-y: auto;
}

.fs-drop-enter-active,
.fs-drop-leave-active {
    transition: all 0.12s ease;
}

.fs-drop-enter-from,
.fs-drop-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.fs-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.375rem 0.5rem;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-primary);
    font-size: var(--text-xs);
    font-family: inherit;
    cursor: pointer;
    transition: background 0.1s;
    text-align: left;
    white-space: nowrap;
}

.fs-option:hover,
.fs-option--highlighted {
    background: var(--bg-hover);
}

.fs-option--selected {
    color: var(--color-primary);
    font-weight: 500;
}

.fs-option__label {
    flex: 1;
}

.fs-option__check {
    width: 13px;
    height: 13px;
    color: var(--color-primary);
    flex-shrink: 0;
}
</style>
